import Swal from 'sweetalert2';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, Col, Form, Row } from 'react-bootstrap';
import Select from 'react-select';
import { Button as ChakraButton } from '@chakra-ui/react';
import { FaSearch } from 'react-icons/fa';
import Pagination from '@material-ui/lab/Pagination';

import {
    IDefaultValue,
    ICompanyDefaultValue,
} from '../../../models/DefaultValue';
import { ApplicationState } from '../../../store';
import { CompanyMaster } from '../../../models/CompanyMaster';
import { useAdminContext } from '../../../context/AdminProvider';
import { logAdminAction } from '../../../helpers/adminLog';
import AdminActionLogModal from '../../log/AdminActionLogModal';

import TrapezoidHeader from './TrapezoidHeader';
import DefaultValueTable from './DefaultValuesTable';
import useEditDefaultValue from './useEditDefaultValue';
import useDefaultValueModal from './useDefaultValueModal';
import useCreateDefaultValue from './useCreateDefaultValue';
import useDeleteDefaultValue from './useDeleteDefaultValue';
import ManageDefaultValueModal from './ManageDefaultValueModal';
import useCompanyDefaultValues from './useCompanyDefaultValues';

interface SelectOption {
    value: string;
    label: string;
}

const selectMenuStyle = { menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) };

function ConfigureDefaultValues() {
    const [searchSid, setSearchSid] = useState('');
    const [searchKey, setSearchKey] = useState('');
    const [activeFilter, setActiveFilter] = useState({ sid: '', key: '' });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [isLogOpen, setIsLogOpen] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const companies = useSelector<ApplicationState, CompanyMaster[]>(
        state => state.companyMaster?.responseCompanyMaster?.result_list ?? []
    );
    const { userRole } = useAdminContext();

    const companyOptions: SelectOption[] = useMemo(() => {
        if (companies.length === 0 || userRole.length === 0) return [];
        const hasMasterSid = userRole.some(r => r.sid === '*');
        if (hasMasterSid) return companies.map(c => ({ value: c.sid, label: c.company_name }));
        const allowedSids = new Set(userRole.map(r => r.sid));
        return companies.filter(c => allowedSids.has(c.sid)).map(c => ({ value: c.sid, label: c.company_name }));
    }, [companies, userRole]);

    const isCompanyLocked = companyOptions.length === 1;

    useEffect(() => {
        if (companyOptions.length === 1) {
            setSearchSid(companyOptions[0].value);
            setActiveFilter(prev => ({ ...prev, sid: companyOptions[0].value }));
        }
    }, [companyOptions]);

    const { modalState, openModal, closeModal, handleChange, handleSidChange } =
        useDefaultValueModal();

    const { getDefaultValues, mappedDefaultValues: companyDefaultValues } =
        useCompanyDefaultValues();
    const { createDefaultValue } = useCreateDefaultValue();
    const { editDefaultValue } = useEditDefaultValue();
    const { deleteDefaultValue } = useDeleteDefaultValue();

    const filteredData = useMemo(() => {
        return companyDefaultValues.filter(d => {
            if (activeFilter.sid && d.sid !== activeFilter.sid) return false;
            if (activeFilter.key && !d.key.toLowerCase().includes(activeFilter.key.toLowerCase())) return false;
            return true;
        });
    }, [companyDefaultValues, activeFilter]);

    const totalPages = Math.ceil(filteredData.length / pageSize);
    const pagedData = filteredData.slice((page - 1) * pageSize, page * pageSize);

    const handleSearch = () => {
        setActiveFilter({ sid: searchSid, key: searchKey });
        setPage(1);
        setHasSearched(true);
    };

    const handleClickCreateDefaultValue = useCallback(() => {
        const initialSid = isCompanyLocked ? companyOptions[0].value : '';
        openModal(
            'Create Default Value',
            'Confirm create',
            undefined,
            async (defaultValue: IDefaultValue) => {
                const success = await createDefaultValue(defaultValue);

                if (success) {
                    closeModal();
                    getDefaultValues();
                    logAdminAction('ADM-DMS104', 'CREATE', defaultValue.key, defaultValue.sid, `value=${defaultValue.value}`);
                    Swal.fire({ title: 'Success', icon: 'success' });
                }
            },
            { sid: initialSid }
        );
    }, [isCompanyLocked, companyOptions]);

    const handleClickEditDefaultValue = useCallback(
        (defaultValue: ICompanyDefaultValue) => {
            openModal(
                'Edit Default Value',
                'Save & Apply',
                defaultValue,
                async (dv: IDefaultValue) => {
                    const success = await editDefaultValue(dv);

                    if (success) {
                        closeModal();
                        getDefaultValues();
                        logAdminAction('ADM-DMS104', 'UPDATE', dv.key, dv.sid, `value=${dv.value}`);
                        Swal.fire({ title: 'Success', icon: 'success' });
                    }
                }
            );
        },
        []
    );

    const handleClickDeleteDefaultValue = useCallback(
        (defaultValue: ICompanyDefaultValue) => {
            Swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Confirm',
            }).then(async (result) => {
                if (result.isConfirmed) {
                    const success = await deleteDefaultValue(defaultValue);

                    if (success) {
                        getDefaultValues();
                        logAdminAction('ADM-DMS104', 'DELETE', defaultValue.key, defaultValue.sid);
                        Swal.fire({ title: 'Deleted!', icon: 'success' });
                    }
                }
            });
        },
        []
    );

    return (
        <>
            <TrapezoidHeader onClickCreate={handleClickCreateDefaultValue} onClickLog={() => setIsLogOpen(true)} />

            <AdminActionLogModal
                isOpen={isLogOpen}
                onClose={() => setIsLogOpen(false)}
                sourceModule="ADM-DMS104"
                defaultSid={searchSid}
            />

            {/* Search + Table */}
            <div className="mat-box employee-font font-size-employee">
                <Row>
                    <Col>
                        <Row className="trapezoid trapezoid-purple" style={{ margin: 'initial' }}>
                            <span style={{ fontSize: '15px' }}>Search Menu</span>
                        </Row>
                        <Card.Body
                            className="mat-box shadow"
                            style={{ borderColor: '#e5e6e9 #dfe0e4 #d0d1d5', borderStyle: 'solid', borderWidth: '1px' }}
                        >
                            <Row>
                                <Col xs={5}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>บริษัท :</Form.Label>
                                        <Select
                                            menuPortalTarget={document.body}
                                            styles={selectMenuStyle}
                                            placeholder="เลือก บริษัท"
                                            options={companyOptions}
                                            value={companyOptions.find(c => c.value === searchSid) ?? null}
                                            onChange={opt => setSearchSid(opt?.value ?? '')}
                                            isClearable={!isCompanyLocked}
                                            isDisabled={isCompanyLocked}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={5}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Key :</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="ค้นหา Key"
                                            value={searchKey}
                                            onChange={e => setSearchKey(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && handleSearch()}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={2} className="d-flex align-items-end pb-3">
                                    <ChakraButton
                                        leftIcon={<FaSearch />}
                                        colorScheme="green"
                                        size="sm"
                                        onClick={handleSearch}
                                    >
                                        ค้นหา
                                    </ChakraButton>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Col>
                </Row>

                <br />

                <Row>
                    <Col xs={6}>
                        <div className="d-flex justify-content-start my-2 my-lg-0 align-items-center">
                            <label style={{ fontSize: '14px', marginBottom: 0 }}>Show&nbsp;&nbsp;</label>
                            <select
                                className="form-control form-control-sm d-inline-block"
                                style={{ width: 'auto' }}
                                value={pageSize}
                                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                            <label style={{ fontSize: '14px', marginBottom: 0 }}>&nbsp;&nbsp;entries</label>
                        </div>
                    </Col>
                    <Col xs={6}>
                        <div className="d-flex justify-content-end my-2 my-lg-0">
                            <Pagination
                                count={totalPages}
                                shape="rounded"
                                showFirstButton
                                showLastButton
                                page={page}
                                onChange={(_, value) => setPage(value)}
                            />
                        </div>
                    </Col>
                </Row>

                <Row className="mt-2">
                    <Col>
                        <DefaultValueTable
                            data={hasSearched ? pagedData : []}
                            emptyMessage={hasSearched ? 'No Data.' : 'กรุณากด ค้นหา เพื่อแสดงข้อมูล'}
                            onClickEdit={handleClickEditDefaultValue}
                            onClickDelete={handleClickDeleteDefaultValue}
                        />
                    </Col>
                </Row>
            </div>

            <ManageDefaultValueModal
                isOpen={modalState.isOpen}
                title={modalState.title}
                buttonText={modalState.buttonText}
                defaultValue={modalState.defaultValue}
                formDefaultValue={modalState.formDefaultValue}
                companyOptions={companyOptions}
                toggle={closeModal}
                onConfirm={modalState.confirmFunction}
                onChange={handleChange}
                onSidChange={handleSidChange}
            />
        </>
    );
}

export default ConfigureDefaultValues;
